import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelSelect as _vModelSelect, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "portal-page auth-page find-id-page" }
const _hoisted_2 = { class: "page-shell auth-page__shell" }
const _hoisted_3 = { class: "auth-form__body" }
const _hoisted_4 = { class: "auth-form__field" }
const _hoisted_5 = {
  key: 0,
  class: "auth-form__msg auth-form__msg--warn"
}
const _hoisted_6 = { class: "auth-form__field" }
const _hoisted_7 = {
  key: 0,
  class: "auth-form__field"
}
const _hoisted_8 = { class: "auth-form__field-row" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = ["disabled"]
const _hoisted_11 = {
  key: 0,
  class: "auth-form__msg auth-form__msg--warn"
}
const _hoisted_12 = {
  key: 1,
  class: "auth-form__msg auth-form__msg--ok"
}
const _hoisted_13 = {
  key: 1,
  class: "auth-form__field"
}
const _hoisted_14 = { class: "auth-form__field-row" }
const _hoisted_15 = ["disabled"]
const _hoisted_16 = ["disabled"]
const _hoisted_17 = {
  key: 0,
  class: "auth-form__msg auth-form__msg--warn"
}
const _hoisted_18 = {
  key: 2,
  class: "auth-form__field"
}
const _hoisted_19 = { class: "auth-form__field-row" }
const _hoisted_20 = ["disabled"]
const _hoisted_21 = ["disabled"]
const _hoisted_22 = {
  key: 0,
  class: "auth-form__hint"
}
const _hoisted_23 = { class: "auth-form__msg auth-form__msg--muted" }
const _hoisted_24 = {
  key: 0,
  class: "auth-form__verify-timer"
}
const _hoisted_25 = {
  key: 0,
  class: "auth-form__msg auth-form__msg--warn"
}
const _hoisted_26 = {
  key: 3,
  class: "auth-form__msg auth-form__msg--ok"
}
const _hoisted_27 = {
  key: 4,
  class: "auth-form__result"
}
const _hoisted_28 = { class: "auth-form__actions" }
const _hoisted_29 = ["disabled"]
const _hoisted_30 = { class: "auth-form__links" }

import { computed, reactive, ref, watch } from 'vue'
import { useVerification } from '@/composables/useVerification'
import {
  inputValidationState,
  deferredInputValidationState,
  shouldShowNameFormatWarning,
  isEmailValid,
  isNameValid,
  isPhoneValid,
  isVerifyCodeValid,
  sanitizeDigitsInput,
  sanitizeNameInput,
} from '@/utils/formValidation'


export default /*@__PURE__*/_defineComponent({
  __name: 'FindIdPage',
  setup(__props) {

const form = reactive({
  name: '',
  email: '',
  phone: '',
  verifyCode: '',
})

const verifyMethod = ref<'email' | 'phone'>('email')
const found = ref(false)
const nameTouched = ref(false)
const nameFeedbackAttempted = ref(false)
const verification = useVerification('find-id')

const nameInputState = computed(() =>
  deferredInputValidationState(
    form.name,
    isNameValid(form.name),
    nameTouched.value,
    nameFeedbackAttempted.value,
  ),
)

const showNameWarning = computed(() =>
  shouldShowNameFormatWarning(form.name, nameTouched.value, nameFeedbackAttempted.value),
)

const emailInputState = computed(() =>
  inputValidationState(form.email, {
    isValid: verification.verified,
    isInvalid: Boolean(form.email) && !isEmailValid(form.email),
  }),
)

const phoneInputState = computed(() =>
  inputValidationState(form.phone, {
    isValid: verification.verified,
    isInvalid: Boolean(form.phone) && !isPhoneValid(form.phone),
  }),
)

const verifyCodeInputState = computed(() =>
  inputValidationState(form.verifyCode, isVerifyCodeValid(form.verifyCode)),
)

const isEmailInputLocked = computed(
  () =>
    verification.sending ||
    verification.verified ||
    verification.codeSent,
)

const isPhoneInputLocked = computed(
  () =>
    verification.sending ||
    verification.verified ||
    verification.codeSent,
)

const onNameInput = (event: Event) => {
  form.name = sanitizeNameInput((event.target as HTMLInputElement).value)
}

const onPhoneInput = (event: Event) => {
  form.phone = sanitizeDigitsInput((event.target as HTMLInputElement).value, 11)
}

const onVerifyCodeInput = (event: Event) => {
  form.verifyCode = sanitizeDigitsInput((event.target as HTMLInputElement).value, 6)
}

watch(verifyMethod, () => {
  found.value = false
  form.verifyCode = ''
  verification.reset()
})

watch(
  () => form.email,
  () => {
    if (verifyMethod.value === 'email') {
      form.verifyCode = ''
      verification.reset()
    }
  },
)

watch(
  () => form.phone,
  () => {
    if (verifyMethod.value === 'phone') {
      form.verifyCode = ''
      verification.reset()
    }
  },
)

const sendCode = async () => {
  if (!isNameValid(form.name)) {
    nameFeedbackAttempted.value = true
    return
  }

  found.value = false
  form.verifyCode = ''
  try {
    if (verifyMethod.value === 'email') {
      await verification.sendEmail(form.email)
    } else {
      await verification.sendPhone(form.phone)
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : '인증번호 발송에 실패했습니다.'
    window.alert(message)
  }
}

const verifyCode = async () => {
  try {
    if (verifyMethod.value === 'email') {
      await verification.verifyEmail(form.email, form.verifyCode)
    } else {
      await verification.verifyPhone(form.phone, form.verifyCode)
    }
  } catch {
    // errorMessage는 폼에 표시
  }
}

const findId = () => {
  if (!isNameValid(form.name)) {
    nameFeedbackAttempted.value = true
    return
  }

  if (!verification.verified) return
  found.value = true
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[19] || (_cache[19] = _createElementVNode("header", { class: "auth-page__head" }, [
        _createElementVNode("p", { class: "auth-page__eyebrow" }, "계정"),
        _createElementVNode("h1", { class: "auth-page__title" }, "아이디 찾기"),
        _createElementVNode("p", { class: "auth-page__desc" }, " 가입 시 등록한 정보와 본인 인증으로 아이디를 확인할 수 있습니다. ")
      ], -1)),
      _createElementVNode("form", {
        class: "auth-page__panel auth-form find-id-form",
        onSubmit: _cache[6] || (_cache[6] = _withModifiers(() => {}, ["prevent"]))
      }, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("label", _hoisted_4, [
            _cache[7] || (_cache[7] = _createElementVNode("span", null, "이름", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.name) = $event)),
              type: "text",
              autocomplete: "name",
              placeholder: "이름을 입력해 주세요",
              maxlength: "20",
              class: _normalizeClass(nameInputState.value),
              onInput: onNameInput,
              onBlur: _cache[1] || (_cache[1] = ($event: any) => (nameTouched.value = true))
            }, null, 34), [
              [_vModelText, form.name]
            ]),
            (showNameWarning.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_5, " 한글 2자 이상 입력해 주세요. "))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_6, [
            _cache[9] || (_cache[9] = _createElementVNode("span", null, "인증 방법", -1)),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((verifyMethod).value = $event)),
              class: "find-id-form__select"
            }, [...(_cache[8] || (_cache[8] = [
              _createElementVNode("option", { value: "email" }, "이메일 인증", -1),
              _createElementVNode("option", { value: "phone" }, "휴대폰 인증", -1)
            ]))], 512), [
              [_vModelSelect, verifyMethod.value]
            ])
          ]),
          (verifyMethod.value === 'email')
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _cache[10] || (_cache[10] = _createElementVNode("span", null, "이메일", -1)),
                _createElementVNode("div", _hoisted_8, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.email) = $event)),
                    type: "email",
                    autocomplete: "email",
                    placeholder: "example@email.com",
                    class: _normalizeClass(emailInputState.value),
                    disabled: isEmailInputLocked.value
                  }, null, 10, _hoisted_9), [
                    [_vModelText, form.email]
                  ]),
                  _createElementVNode("button", {
                    type: "button",
                    class: _normalizeClass(["auth-form__inline-btn", { 'auth-form__inline-btn--done': _unref(verification).codeSent }]),
                    disabled: !_unref(isEmailValid)(form.email) || _unref(verification).verified || _unref(verification).sending || _unref(verification).resendIn > 0,
                    onClick: sendCode
                  }, _toDisplayString(_unref(verification).sendActionLabel('인증발송', '재발송')), 11, _hoisted_10)
                ]),
                (verifyMethod.value === 'email' && _unref(verification).errorMessage && !_unref(verification).codeSent)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(_unref(verification).errorMessage), 1))
                  : (verifyMethod.value === 'email' && _unref(verification).codeSent && !_unref(verification).verified)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_12, _toDisplayString(form.email) + "으로 인증번호를 보냈습니다. 받은편지함과 스팸함을 확인해 주세요. ", 1))
                    : _createCommentVNode("", true)
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_13, [
                _cache[11] || (_cache[11] = _createElementVNode("span", null, "휴대폰 번호", -1)),
                _createElementVNode("div", _hoisted_14, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.phone) = $event)),
                    type: "tel",
                    inputmode: "numeric",
                    autocomplete: "tel",
                    placeholder: "01012345678",
                    maxlength: "11",
                    class: _normalizeClass(phoneInputState.value),
                    disabled: isPhoneInputLocked.value,
                    onInput: onPhoneInput
                  }, null, 42, _hoisted_15), [
                    [_vModelText, form.phone]
                  ]),
                  _createElementVNode("button", {
                    type: "button",
                    class: _normalizeClass(["auth-form__inline-btn", { 'auth-form__inline-btn--done': _unref(verification).codeSent }]),
                    disabled: !_unref(isPhoneValid)(form.phone) || _unref(verification).verified || _unref(verification).sending || _unref(verification).resendIn > 0,
                    onClick: sendCode
                  }, _toDisplayString(_unref(verification).sendActionLabel('인증발송', '재발송')), 11, _hoisted_16)
                ]),
                (verifyMethod.value === 'phone' && _unref(verification).errorMessage)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_17, _toDisplayString(_unref(verification).errorMessage), 1))
                  : _createCommentVNode("", true)
              ])),
          (!_unref(verification).verified)
            ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                _cache[13] || (_cache[13] = _createElementVNode("span", null, "인증번호", -1)),
                _createElementVNode("div", _hoisted_19, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.verifyCode) = $event)),
                    type: "text",
                    inputmode: "numeric",
                    maxlength: "6",
                    placeholder: "인증번호 6자리",
                    class: _normalizeClass(verifyCodeInputState.value),
                    disabled: !_unref(verification).codeSent,
                    onInput: onVerifyCodeInput
                  }, null, 42, _hoisted_20), [
                    [_vModelText, form.verifyCode]
                  ]),
                  _createElementVNode("button", {
                    type: "button",
                    class: "auth-form__inline-btn",
                    disabled: !_unref(verification).codeSent || !_unref(isVerifyCodeValid)(form.verifyCode) || _unref(verification).verifying,
                    onClick: verifyCode
                  }, _toDisplayString(_unref(verification).verifying ? '확인중...' : '확인'), 9, _hoisted_21)
                ]),
                (!_unref(verification).codeSent)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_22, " 인증발송 후 인증번호를 입력해 주세요. "))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createElementVNode("p", _hoisted_23, [
                        _cache[12] || (_cache[12] = _createTextVNode(" 인증번호가 발송되었습니다. ", -1)),
                        (_unref(verification).timer > 0)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_24, _toDisplayString(_unref(verification).formatTimer(_unref(verification).timer)), 1))
                          : _createCommentVNode("", true)
                      ]),
                      (_unref(verification).errorMessage)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_25, _toDisplayString(_unref(verification).errorMessage), 1))
                        : _createCommentVNode("", true)
                    ], 64))
              ]))
            : _createCommentVNode("", true),
          (_unref(verification).verified)
            ? (_openBlock(), _createElementBlock("p", _hoisted_26, "본인 인증이 완료되었습니다."))
            : _createCommentVNode("", true),
          (found.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_27, [...(_cache[14] || (_cache[14] = [
                _createElementVNode("p", { class: "auth-form__result-label" }, "조회된 아이디", -1),
                _createElementVNode("p", { class: "auth-form__result-value" }, "jang***", -1),
                _createElementVNode("p", { class: "auth-form__hint" }, "개인정보 보호를 위해 일부만 표시됩니다.", -1)
              ]))]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_28, [
          _createElementVNode("button", {
            type: "button",
            class: "btn-primary auth-form__submit",
            disabled: !_unref(verification).verified,
            onClick: findId
          }, " 아이디 찾기 ", 8, _hoisted_29),
          _createVNode(_component_router_link, {
            to: "/login",
            class: "btn-secondary auth-form__secondary"
          }, {
            default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
              _createTextVNode("로그인", -1)
            ]))]),
            _: 1
          })
        ]),
        _createElementVNode("div", _hoisted_30, [
          _createVNode(_component_router_link, {
            to: "/find-password",
            class: "auth-form__link"
          }, {
            default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
              _createTextVNode("비밀번호 찾기", -1)
            ]))]),
            _: 1
          }),
          _cache[18] || (_cache[18] = _createElementVNode("span", {
            class: "auth-form__divider",
            "aria-hidden": "true"
          }, null, -1)),
          _createVNode(_component_router_link, {
            to: "/signup",
            class: "auth-form__link"
          }, {
            default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
              _createTextVNode("회원가입", -1)
            ]))]),
            _: 1
          })
        ])
      ], 32)
    ])
  ]))
}
}

})